import { supabase } from './supabase';

const MEDIA_BUCKET = 'product-photos';
const MEDIA_PREFIX = 'media-library';

export interface MediaItem {
  id: string;
  business_id: string;
  url: string;
  storage_path: string | null;
  file_name: string | null;
  mime_type: string | null;
  file_size: number | null;
  width: number | null;
  height: number | null;
  /** Video length in seconds; null for images. */
  duration_seconds?: number | null;
  alt: string | null;
  source: string;
  is_deleted: boolean;
  created_at: string;
  /** Legacy GBP marker — prefer `platformUsage` from listMediaWithPlatformUsage. */
  gbp_post_used_at?: string | null;
  gbp_post_draft_id?: string | null;
  gbp_post_history_id?: string | null;
}

export type MediaPlatform = 'google' | 'facebook' | 'instagram' | 'linkedin';

/**
 * Created during Instagram/Facebook compose (AI, crops, review cards).
 * Hidden from Media Library UIs; never claimed by auto-post queues;
 * abandoned session copies are soft-deleted.
 */
export const SOCIAL_COMPOSE_MEDIA_SOURCES = [
  'ai_generate',
  'ai_edit',
  'social_crop',
  'google_review_card',
] as const;

export type SocialComposeMediaSource = (typeof SOCIAL_COMPOSE_MEDIA_SOURCES)[number];

export function isSocialComposeMediaSource(source: unknown): boolean {
  return SOCIAL_COMPOSE_MEDIA_SOURCES.includes(String(source || '') as SocialComposeMediaSource);
}

/** PostgREST `not.in` filter value for compose-only media sources. */
export function socialComposeSourceNotInFilter(): string {
  return `(${SOCIAL_COMPOSE_MEDIA_SOURCES.map((s) => `"${s}"`).join(',')})`;
}

export type MediaPlatformUsageTag = {
  platform: MediaPlatform;
  connectionId: string;
  /** Short label for badges, e.g. "Google · Downtown" or "Instagram". */
  label: string;
  usedAt: string;
};

export type MediaItemWithUsage = MediaItem & {
  platformUsage: MediaPlatformUsageTag[];
};

export type MediaUsageFilter = {
  platform: MediaPlatform;
  connectionId: string;
};

/**
 * CDN-resized version of a Supabase Storage public URL (image transformations).
 * Rewrites /storage/v1/object/public/… → /storage/v1/render/image/public/…
 * Non-storage URLs and GIFs are returned unchanged.
 */
export function transformedImageUrl(
  url: string | null | undefined,
  opts: { width: number; quality?: number },
): string {
  const raw = String(url || '');
  if (!raw) return raw;
  if (/\.gif(\?|#|$)/i.test(raw)) return raw;
  const marker = '/storage/v1/object/public/';
  const idx = raw.indexOf(marker);
  if (idx === -1) return raw;
  const base =
    raw.slice(0, idx) + '/storage/v1/render/image/public/' + raw.slice(idx + marker.length);
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}width=${Math.round(opts.width)}&quality=${opts.quality ?? 75}&resize=contain`;
}

// ── Video ────────────────────────────────────────────────────

/** Social video uploads: Meta accepts MP4/MOV, and 1 GB is the Instagram cap. */
export const VIDEO_MAX_BYTES = 1024 * 1024 * 1024;
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'm4v'];

export function isVideoMime(mime: string | null | undefined): boolean {
  return /^video\//i.test(String(mime || ''));
}

/** A media-library item (or plain file descriptor) that is a video. */
export function isVideoMedia(item: { mime_type?: string | null; url?: string | null }): boolean {
  if (isVideoMime(item.mime_type)) return true;
  return /\.(mp4|mov|m4v)(\?|#|$)/i.test(String(item.url || ''));
}

export function isVideoFile(file: File): boolean {
  if (isVideoMime(file.type)) return true;
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  return !file.type && VIDEO_EXTENSIONS.includes(ext);
}

export function formatDuration(seconds: number | null | undefined): string {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

/** Read width, height, and duration of a video File (best-effort). */
export function readVideoMeta(
  file: File,
): Promise<{ width: number | null; height: number | null; duration: number | null }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    const finish = (meta: { width: number | null; height: number | null; duration: number | null }) => {
      URL.revokeObjectURL(url);
      resolve(meta);
    };
    video.onloadedmetadata = () =>
      finish({
        width: video.videoWidth || null,
        height: video.videoHeight || null,
        duration: Number.isFinite(video.duration) ? Math.round(video.duration * 100) / 100 : null,
      });
    video.onerror = () => finish({ width: null, height: null, duration: null });
    video.src = url;
  });
}

/** Small grid/strip thumbnail (~360px) — use everywhere a tile is rendered. */
export function mediaThumbUrl(url: string | null | undefined): string {
  return transformedImageUrl(url, { width: 360, quality: 70 });
}

/**
 * Working-size preview (~1600px) for crop editors / AI edit sources.
 * Big enough for a 1080px social bake, ~10× smaller than a raw phone photo.
 */
export function mediaPreviewUrl(url: string | null | undefined): string {
  return transformedImageUrl(url, { width: 1600, quality: 82 });
}

/**
 * Downscale/re-encode an image before upload (max edge 2560, JPEG 0.85).
 * Raw phone photos shrink ~10×; small or non-image files pass through untouched.
 */
async function compressForUpload(file: File): Promise<File> {
  const SKIP_BYTES = 600 * 1024;
  const MAX_EDGE = 2560;
  if (!file.type.startsWith('image/')) return file;
  if (/gif|svg/i.test(file.type)) return file;
  try {
    const bitmap = await createImageBitmap(file);
    try {
      const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height, 1));
      if (scale === 1 && file.size <= SKIP_BYTES) return file;
      const w = Math.max(1, Math.round(bitmap.width * scale));
      const h = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return file;
      ctx.drawImage(bitmap, 0, 0, w, h);
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 0.85),
      );
      if (!blob || blob.size >= file.size) return file;
      const name = (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
      return new File([blob], name, { type: 'image/jpeg' });
    } finally {
      bitmap.close();
    }
  } catch {
    // Undecodable format (e.g. HEIC in some browsers) — upload as-is.
    return file;
  }
}

/** Read pixel dimensions of an image File (best-effort; resolves null on failure). */
function readImageSize(file: File): Promise<{ width: number | null; height: number | null }> {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) {
      resolve({ width: null, height: null });
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth || null, height: img.naturalHeight || null });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      resolve({ width: null, height: null });
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

/**
 * List non-deleted media for a business, newest first.
 * Compose-only assets (AI generate/edit, social crops, review cards) are
 * excluded by default so they never appear in Media Library pickers/browsers.
 */
export async function listMedia(
  businessId: string,
  options?: { includeComposeSources?: boolean },
): Promise<MediaItem[]> {
  let query = supabase
    .from('media_library')
    .select('*')
    .eq('business_id', businessId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: false });

  if (!options?.includeComposeSources) {
    query = query.not('source', 'in', socialComposeSourceNotInFilter());
  }

  const { data, error } = await query;

  if (error) throw error;
  return (data || []) as MediaItem[];
}

/**
 * List media with per-platform / per-location usage tags
 * (Google once per location, Facebook/Instagram once per connected account).
 */
export async function listMediaWithPlatformUsage(
  businessId: string,
): Promise<MediaItemWithUsage[]> {
  const items = await listMedia(businessId);
  if (items.length === 0) return [];

  const mediaIds = items.map((m) => m.id);

  const [gbpRes, socialRes] = await Promise.all([
    supabase
      .from('media_library_gbp_usage')
      .select('media_id, connection_id, used_at, google_business_connections(location_name)')
      .eq('business_id', businessId)
      .in('media_id', mediaIds),
    supabase
      .from('media_library_social_usage')
      .select(
        'media_id, connection_id, used_at, social_media_connections(platform, display_name, username)',
      )
      .eq('business_id', businessId)
      .in('media_id', mediaIds),
  ]);

  if (gbpRes.error) console.warn('Failed to load GBP media usage:', gbpRes.error);
  if (socialRes.error) console.warn('Failed to load social media usage:', socialRes.error);

  const byMedia = new Map<string, MediaPlatformUsageTag[]>();

  for (const row of gbpRes.data || []) {
    const mediaId = row.media_id as string;
    const conn = row.google_business_connections as
      | { location_name?: string | null }
      | { location_name?: string | null }[]
      | null;
    const loc =
      (Array.isArray(conn) ? conn[0]?.location_name : conn?.location_name)?.trim() ||
      'Google location';
    const tag: MediaPlatformUsageTag = {
      platform: 'google',
      connectionId: row.connection_id as string,
      label: `Google · ${loc}`,
      usedAt: (row.used_at as string) || '',
    };
    const list = byMedia.get(mediaId) || [];
    list.push(tag);
    byMedia.set(mediaId, list);
  }

  for (const row of socialRes.data || []) {
    const mediaId = row.media_id as string;
    const conn = row.social_media_connections as
      | { platform?: string; display_name?: string | null; username?: string | null }
      | { platform?: string; display_name?: string | null; username?: string | null }[]
      | null;
    const c = Array.isArray(conn) ? conn[0] : conn;
    const platformRaw = String(c?.platform || '').toLowerCase();
    if (platformRaw !== 'facebook' && platformRaw !== 'instagram' && platformRaw !== 'linkedin') continue;
    const account =
      (c?.display_name || c?.username || '').trim() ||
      (platformRaw === 'facebook' ? 'Facebook' : platformRaw === 'linkedin' ? 'LinkedIn' : 'Instagram');
    const shortPlatform =
      platformRaw === 'facebook' ? 'Facebook' : platformRaw === 'linkedin' ? 'LinkedIn' : 'Instagram';
    const label =
      account.toLowerCase() === shortPlatform.toLowerCase()
        ? shortPlatform
        : `${shortPlatform} · ${account}`;
    const tag: MediaPlatformUsageTag = {
      platform: platformRaw,
      connectionId: row.connection_id as string,
      label,
      usedAt: (row.used_at as string) || '',
    };
    const list = byMedia.get(mediaId) || [];
    list.push(tag);
    byMedia.set(mediaId, list);
  }

  return items.map((item) => ({
    ...item,
    platformUsage: byMedia.get(item.id) || [],
  }));
}

/** True if this media is already used for the given platform connection/location. */
export function isMediaUsedForConnection(
  item: MediaItemWithUsage | { platformUsage?: MediaPlatformUsageTag[] },
  connectionId: string,
): boolean {
  if (!connectionId) return false;
  return (item.platformUsage || []).some((u) => u.connectionId === connectionId);
}

/** Upload one or more files into the library and return the created rows. */
export async function uploadMedia(
  businessId: string,
  files: File[],
  options?: { source?: string; alt?: string | null },
): Promise<MediaItem[]> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id ?? null;
  const source = options?.source || 'upload';
  const alt = options?.alt ?? null;

  const created: MediaItem[] = [];

  for (const rawFile of files) {
    const video = isVideoFile(rawFile);
    if (video && rawFile.size > VIDEO_MAX_BYTES) {
      throw new Error(
        `"${rawFile.name}" is ${(rawFile.size / (1024 * 1024)).toFixed(0)} MB — videos must be under 1 GB.`,
      );
    }
    const file = video ? rawFile : await compressForUpload(rawFile);
    const ext = (file.name.split('.').pop() || 'bin').toLowerCase();
    const unique = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const storagePath = `${MEDIA_PREFIX}/${businessId}/${unique}.${ext}`;

    // Paths are unique per upload, so objects are immutable — cache for a year.
    const { error: uploadError } = await supabase.storage
      .from(MEDIA_BUCKET)
      .upload(storagePath, file, { cacheControl: '31536000', upsert: false });
    if (uploadError) throw uploadError;

    const {
      data: { publicUrl },
    } = supabase.storage.from(MEDIA_BUCKET).getPublicUrl(storagePath);

    const imageSize = video ? null : await readImageSize(file);
    const videoMeta = video ? await readVideoMeta(file) : null;
    const width = imageSize?.width ?? videoMeta?.width ?? null;
    const height = imageSize?.height ?? videoMeta?.height ?? null;

    const { data: row, error: insertError } = await supabase
      .from('media_library')
      .insert({
        business_id: businessId,
        url: publicUrl,
        storage_path: storagePath,
        file_name: file.name,
        mime_type: file.type || (video ? 'video/mp4' : null),
        file_size: file.size || null,
        width,
        height,
        duration_seconds: videoMeta?.duration ?? null,
        source,
        alt,
        created_by: userId,
      })
      .select()
      .single();

    if (insertError) {
      await supabase.storage.from(MEDIA_BUCKET).remove([storagePath]);
      throw insertError;
    }

    created.push(row as MediaItem);
  }

  return created;
}

/** Soft-delete a library item (keeps the object so existing references don't 404). */
export async function deleteMedia(id: string): Promise<void> {
  const { error } = await supabase
    .from('media_library')
    .update({ is_deleted: true, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}
